class SDScienceStatCounter {
  constructor(root) {
    this.root = root;
    this.stats = Array.from(root.querySelectorAll('[data-sd-science-stat]')).filter(
      (stat) => !stat.closest('.sd-science__card--clone')
    );
    this.animated = new WeakSet();
    this.observer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.duration = 1800;
  }

  parseValue(value) {
    const match = String(value || '').trim().match(/^(\d+(?:[.,]\d+)?)(.*)$/u);
    if (!match) return null;

    const numberPart = match[1];
    const decimals = numberPart.includes('.')
      ? numberPart.split('.')[1].length
      : numberPart.includes(',')
        ? numberPart.split(',')[1].length
        : 0;

    return {
      target: parseFloat(numberPart.replace(',', '.')),
      suffix: match[2] || '',
      decimals,
      raw: String(value).trim(),
    };
  }

  formatValue(current, parsed) {
    const value =
      parsed.decimals > 0
        ? current.toFixed(parsed.decimals).replace('.', ',')
        : Math.round(current).toString();

    return `${value}${parsed.suffix}`;
  }

  animateStat(stat) {
    if (this.animated.has(stat)) return;

    const parsed = this.parseValue(stat.dataset.sdScienceStatValue);
    if (!parsed || Number.isNaN(parsed.target)) return;

    this.animated.add(stat);

    if (this.reducedMotion) {
      stat.textContent = parsed.raw;
      return;
    }

    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = parsed.target * eased;

      stat.textContent = this.formatValue(current, parsed);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        stat.textContent = parsed.raw;
      }
    };

    stat.textContent = this.formatValue(0, parsed);
    requestAnimationFrame(tick);
  }

  connect() {
    if (!this.stats.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.animateStat(entry.target);
          this.observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -5% 0px',
      }
    );

    this.stats.forEach((stat) => {
      const parsed = this.parseValue(stat.dataset.sdScienceStatValue);
      if (!parsed || Number.isNaN(parsed.target)) return;

      if (this.reducedMotion) {
        stat.textContent = parsed.raw;
        return;
      }

      stat.textContent = this.formatValue(0, parsed);
      this.observer.observe(stat);
    });
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

class SDScienceMobileLoop {
  constructor(root) {
    this.root = root;
    this.scroller = root.querySelector('.sd-science__grid');
    this.active = false;
    this.jumping = false;
    this.clones = [];
    this.originals = [];
    this.settleTimer = null;

    this.mql = window.matchMedia('(max-width: 749px)');
    this.onMedia = () => this.sync();
    this.onScroll = () => {
      if (this.jumping) return;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => this.settle(), 120);
    };

    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onMedia);
    } else if (this.mql.addListener) {
      this.mql.addListener(this.onMedia);
    }
  }

  sync() {
    if (this.mql.matches) {
      this.enable();
    } else {
      this.disable();
    }
  }

  getOriginalCards() {
    if (!this.scroller) return [];

    return Array.from(this.scroller.children).filter(
      (child) =>
        child.classList.contains('sd-science__card') &&
        !child.classList.contains('sd-science__card--clone')
    );
  }

  fixSvgMaskIds(card) {
    card.querySelectorAll('mask[id]').forEach((mask) => {
      const oldId = mask.id;
      const newId = `${oldId}-c-${Math.random().toString(36).slice(2, 7)}`;
      mask.id = newId;
      card.querySelectorAll(`[mask="url(#${oldId})"]`).forEach((element) => {
        element.setAttribute('mask', `url(#${newId})`);
      });
    });
  }

  makeClones() {
    return this.originals.map((card) => {
      const clone = card.cloneNode(true);
      clone.classList.add('sd-science__card--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      this.fixSvgMaskIds(clone);
      return clone;
    });
  }

  enable() {
    if (this.active || !this.scroller) return;

    this.originals = this.getOriginalCards();
    if (this.originals.length < 2) return;

    this.active = true;

    const leadFragment = document.createDocumentFragment();
    const trailFragment = document.createDocumentFragment();
    const lead = this.makeClones();
    const trail = this.makeClones();

    lead.forEach((clone) => leadFragment.appendChild(clone));
    trail.forEach((clone) => trailFragment.appendChild(clone));

    this.scroller.insertBefore(leadFragment, this.originals[0]);
    this.scroller.appendChild(trailFragment);
    this.clones = lead.concat(trail);

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    window.requestAnimationFrame(() => {
      this.centerOriginal(Math.floor(this.originals.length / 2));
    });
  }

  disable() {
    if (!this.active || !this.scroller) return;

    this.active = false;
    this.scroller.removeEventListener('scroll', this.onScroll);
    window.clearTimeout(this.settleTimer);

    this.clones.forEach((clone) => clone.remove());
    this.clones = [];
    this.originals = [];
    this.scroller.scrollLeft = 0;
  }

  centerCard(card) {
    if (!card || !this.scroller) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const delta =
      cardRect.left + cardRect.width / 2 - (scrollerRect.left + this.scroller.clientWidth / 2);

    const previous = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft += delta;
    this.scroller.style.scrollBehavior = previous;
  }

  centerOriginal(index) {
    if (!this.scroller || !this.originals.length) return;

    const child = this.scroller.children[this.originals.length + index];
    this.centerCard(child);
  }

  settle() {
    if (!this.active || !this.scroller) return;

    const children = Array.from(this.scroller.children);
    const len = this.originals.length;

    if (children.length < len * 3) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const center = scrollerRect.left + this.scroller.clientWidth / 2;

    let bestIndex = 0;
    let bestDistance = Infinity;

    children.forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    const setWidth =
      children[len].getBoundingClientRect().left - children[0].getBoundingClientRect().left;

    if (bestIndex < len) {
      this.jumpBy(setWidth);
    } else if (bestIndex >= len * 2) {
      this.jumpBy(-setWidth);
    }
  }

  jumpBy(delta) {
    if (!delta || !this.scroller) return;

    this.jumping = true;
    const previous = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft += delta;
    this.scroller.style.scrollBehavior = previous;

    window.requestAnimationFrame(() => {
      this.jumping = false;
    });
  }

  recenterNearest() {
    if (!this.scroller) return;

    const children = Array.from(this.scroller.children);
    if (!children.length) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const center = scrollerRect.left + this.scroller.clientWidth / 2;

    let best = null;
    let bestDistance = Infinity;

    children.forEach((child) => {
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = child;
      }
    });

    this.centerCard(best);
  }

  onResize() {
    if (!this.active) return;
    this.recenterNearest();
  }

  destroy() {
    this.disable();

    if (this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this.onMedia);
    } else if (this.mql.removeListener) {
      this.mql.removeListener(this.onMedia);
    }
  }
}

class SDScience extends HTMLElement {
  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
    this.mobileLoop = null;
    this.statCounter = null;
  }

  connectedCallback() {
    this.mobileLoop = new SDScienceMobileLoop(this);
    this.mobileLoop.sync();
    this.statCounter = new SDScienceStatCounter(this);
    this.statCounter.connect();
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.statCounter?.destroy();
    this.mobileLoop?.destroy();
  }

  onResize() {
    this.mobileLoop?.sync();
    this.mobileLoop?.onResize();
  }
}

if (!customElements.get('sd-science')) {
  customElements.define('sd-science', SDScience);
}
